import React, { forwardRef } from 'react'
import styled from '@emotion/styled'
import Caption from 'modules/ui/atoms/Typography/Caption'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import Label from 'modules/ui/atoms/Typography/Label'
import RadioButton from '../RadioButton'

export interface RadioGroupProps {
  /** The id for the radio group. Used for accessibility */
  id?: string
  /** The label for the radio group. */
  label?: string
  /** The name of the radio group. Used for accessibility and keyboard binding. */
  name: string
  /** An array of options to be displayed as radio buttons. */
  options: { label: string; key: string; disabled?: boolean }[]
  /** The value of the currently selected radio button. */
  value: string
  /** A function to be called when the selected radio button changes. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (...event: any[]) => void
  /** An error message to be displayed if there is an error */
  error?: string
  /** Whether the field is required to be filled out or not */
  required?: boolean
  dataId?: string
  autoFocus?: boolean
  /** Whether this is being used in an embedded campaign */
  isEmbedded?: boolean
  onFocus?: () => void
}

const RadioGroupContainer = styled.fieldset<{ isEmbedded?: boolean }>`
  border: none;
  margin: 0;
  padding: 0;

  div.buttons {
    gap: 16px;
    display: flex;

    ${({ isEmbedded }) =>
      !isEmbedded &&
      `
      justify-content: flex-start;
      margin-top: 8px;
    `}
  }
`

/**
 * A group of radio buttons.
 */
const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      id,
      label,
      options,
      value,
      onChange,
      name,
      error,
      required,
      dataId = '',
      autoFocus,
      isEmbedded,
      onFocus,
    },
    ref,
  ) => (
    <RadioGroupContainer role="radiogroup" aria-labelledby={id} isEmbedded={isEmbedded}>
      {label && (
        <Label id={id} errored={!!error} size="sm" style={{ margin: '12px 0 0' }}>
          {label}
        </Label>
      )}

      <div className="buttons" ref={ref}>
        {options.map((option, index) => (
          <RadioButton
            key={option.key}
            label={option.label}
            value={option.key}
            checked={option.key === value}
            onChange={onChange}
            disabled={option.disabled}
            radioGroupName={name}
            dataId={dataId}
            autoFocus={autoFocus && index === 0}
            hasBorder={isEmbedded}
            onFocus={index === 0 ? onFocus : undefined}
          />
        ))}
      </div>

      {error && (
        <Caption errored={!!error} style={{ paddingTop: '4px' }}>
          <span aria-hidden="true">
            <UiIcon
              aria-hidden="true"
              name="error"
              color={DEFAULT_THEME_COLORS['--theme-color--status-error']}
              w={16}
              h={16}
            />
          </span>
          {error}
        </Caption>
      )}
    </RadioGroupContainer>
  ),
)

RadioGroup.displayName = 'RadioGroup'

export default RadioGroup
